import { D, dec, money, PAYMENT_METHODS, ZERO } from '@makila/fiscal';
/**
 * Caixa: abertura com fundo, sangrias, reforços, abertura de gaveta sem
 * venda, relatório X (a meio) e Z (fecho) — docs/09-POS-PROPOSTA.md §4 (Núcleo).
 *
 * O numerário esperado na gaveta é:
 *   fundo + numerário das vendas (já líquido de troco) − numerário devolvido
 *   + reforços − sangrias + gorjetas em numerário
 * e a diferença do fecho é contado − esperado. As gorjetas (decisão de
 * 21/10/2026) ficam FORA dos documentos fiscais: registam-se aqui, por
 * empregado, e o Z diz quanto entregar a cada um. Os outros meios (M-Pesa,
 * cartão…) não estão na gaveta: entram no relatório, não na diferença.
 */
/** Notas e moedas do metical em circulação (Banco de Moçambique). */
export const MZN_DENOMINATIONS = ['1000', '500', '200', '100', '50', '20', '10', '5', '2', '1', '0.50'];
export const CASH_MOVEMENT_LABEL = { cash_in: 'Reforço', cash_out: 'Sangria', drawer_open: 'Abertura de gaveta sem venda', tip: 'Gorjeta' };
export class CashError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'CashError';
    }
}
/** Total contado a partir das quantidades por denominação. */
export function countTotal(counted) {
    let t = ZERO;
    for (const [denom, n] of Object.entries(counted)) {
        if (!MZN_DENOMINATIONS.includes(denom))
            throw new CashError('invalid_count', `Denominação desconhecida: ${denom}`);
        if (!Number.isInteger(n) || n < 0)
            throw new CashError('invalid_count', `Quantidade inválida para ${denom} MT`);
        t = t.plus(new D(denom).times(n));
    }
    return money(t);
}
export function assertAmount(v, allowZero = false) {
    const a = dec(v);
    if (a.isNegative() || (!allowZero && a.isZero()) || a.decimalPlaces() > 2)
        throw new CashError('invalid_amount', 'Valor inválido');
    return money(a);
}
/**
 * Relatório X (sessão aberta) ou Z (fechada). As notas de crédito entram com
 * sinal negativo; o numerário que devolvem sai da gaveta.
 */
export function cashReport(session, docs, movements) {
    const mine = docs.filter((d) => d.cashSessionId === session.id).sort((a, b) => a.systemEntryDate.localeCompare(b.systemEntryDate) || a.number - b.number);
    const byType = {};
    const byMethod = new Map();
    const byRate = new Map();
    let gross = ZERO;
    for (const d of mine) {
        const sign = d.docType === 'NC' ? -1 : 1;
        const t = byType[d.docType] ?? { count: 0, total: '0.00' };
        byType[d.docType] = { count: t.count + 1, total: money(new D(t.total).plus(new D(d.grossTotal).times(sign))) };
        // O recibo é dinheiro que entra, não é venda: conta nos meios de pagamento, não no total vendido.
        if (d.docType !== 'RE')
            gross = gross.plus(new D(d.grossTotal).times(sign));
        for (const p of d.payments)
            byMethod.set(p.method, (byMethod.get(p.method) ?? ZERO).plus(new D(p.amount).times(sign)));
        for (const r of d.taxSummary) {
            const cur = byRate.get(r.rate) ?? { net: ZERO, tax: ZERO };
            byRate.set(r.rate, { net: cur.net.plus(new D(r.netAmount).times(sign)), tax: cur.tax.plus(new D(r.taxAmount).times(sign)) });
        }
    }
    const sum = (k) => movements.filter((m) => m.sessionId === session.id && m.kind === k).reduce((s, m) => s.plus(m.amount), ZERO);
    const cashIn = sum('cash_in');
    const cashOut = sum('cash_out');
    const tipRows = movements.filter((m) => m.sessionId === session.id && m.kind === 'tip');
    const tipsBy = new Map();
    for (const t of tipRows) {
        const cur = tipsBy.get(t.beneficiaryId ?? t.operatorId) ?? { cash: ZERO, other: ZERO };
        tipsBy.set(t.beneficiaryId ?? t.operatorId, (t.method ?? 'NU') === 'NU' ? { ...cur, cash: cur.cash.plus(t.amount) } : { ...cur, other: cur.other.plus(t.amount) });
    }
    const cashTips = [...tipsBy.values()].reduce((s, v) => s.plus(v.cash), ZERO);
    const expected = new D(session.openingFloat).plus(byMethod.get('NU') ?? ZERO).plus(cashIn).minus(cashOut).plus(cashTips);
    const closed = session.status === 'closed';
    return {
        sessionId: session.id,
        kind: closed ? 'Z' : 'X',
        zNumber: session.zNumber,
        openedAt: session.openedAt,
        closedAt: session.closedAt,
        openedBy: session.operatorId,
        closedBy: session.closedBy,
        openingFloat: money(session.openingFloat),
        documents: { count: mine.length, first: mine[0]?.fullNumber ?? null, last: mine.at(-1)?.fullNumber ?? null, byType },
        grossTotal: money(gross),
        byMethod: [...byMethod.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([method, amount]) => ({ method, label: PAYMENT_METHODS[method], amount: money(amount) })),
        byTaxRate: [...byRate.entries()].sort(([a], [b]) => new D(b).comparedTo(new D(a))).map(([rate, v]) => ({ rate, netAmount: money(v.net), taxAmount: money(v.tax) })),
        cashIn: money(cashIn),
        cashOut: money(cashOut),
        drawerOpens: movements.filter((m) => m.sessionId === session.id && m.kind === 'drawer_open').length,
        tips: [...tipsBy.entries()].map(([beneficiaryId, v]) => ({ beneficiaryId, cash: money(v.cash), other: money(v.other) })),
        expectedCash: money(expected),
        countedCash: closed ? session.countedCash : null,
        difference: closed ? session.difference : null,
    };
}
//# sourceMappingURL=cash.js.map